<template>
  <div class="statistics-chart" :style="{height: height}">
    <ChartComponent :key="chartKey" :type="type" :data="chartData" :options="chartOptions" />
  </div>
</template>

<script>
import {Chart as ChartComponent} from "vue-chartjs";
import {
  ArcElement, BarController, BarElement, CategoryScale, Chart, DoughnutController, Filler, Legend,
  LineController, LineElement, LinearScale, PointElement, Tooltip
} from "chart.js";
import {formatValue, moneyCompact} from "@/statistics/utils/format";
import {PALETTE, colorAt, withAlpha} from "@/statistics/utils/chartTheme";

Chart.register(ArcElement, BarController, BarElement, CategoryScale, DoughnutController, Filler, Legend, LineController, LineElement, LinearScale, PointElement, Tooltip);
Chart.defaults.font.family = "inherit";
Chart.defaults.color = "#626c82";

/**
 * Graphique générique (barres, courbes, anneau, ou barres + courbes) aux couleurs et formats de l'application.
 *
 * datasets : [{label, data, type?: 'bar'|'line', color?, colors? (anneau), yAxisID?: 'y1' (second axe, à droite), dashed?}]
 * valueFormat : genre des valeurs (money, integer, percent, days) pour les infobulles et les axes
 * sortTooltip : classe les valeurs de l'infobulle de la plus grande à la plus petite (courbes superposées)
 */
export default {
  name: 'StatisticsChart',
  components: {ChartComponent},
  props: {
    type: {type: String, default: 'bar'},
    labels: {type: Array, required: true},
    datasets: {type: Array, required: true},
    valueFormat: {type: String, default: 'money'},
    secondaryFormat: {type: String, default: 'integer'},
    stacked: {type: Boolean, default: false},
    sortTooltip: {type: Boolean, default: false},
    horizontal: {type: Boolean, default: false},
    legend: {type: Boolean, default: undefined},
    height: {type: String, default: '280px'},
  },
  computed: {
    // vue-chartjs rapproche les séries par libellé : quand elles changent de place ou de type (barres <-> courbe en changeant de CA),
    // Chart.js perd le contrôleur d'une série déjà ordonnée (« buildOrUpdateElements » de null). On recrée donc le graphique dans ce cas.
    chartKey() {
      return [this.type, ...this.datasets.map(dataset => `${dataset.label}:${dataset.type ?? this.type}`)].join('|');
    },
    isDoughnut() {
      return this.type === 'doughnut';
    },
    chartData() {
      return {
        labels: this.labels,
        datasets: this.datasets.map((dataset, index) => this.style(dataset, index)),
      };
    },
    chartOptions() {
      const hasSecondAxis = this.datasets.some(dataset => dataset.yAxisID === 'y1');
      const showLegend = this.legend ?? (this.isDoughnut || this.datasets.length > 1);
      const options = {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {mode: this.isDoughnut ? 'nearest' : 'index', intersect: false},
        plugins: {
          legend: {display: showLegend, position: this.isDoughnut ? 'right' : 'bottom', labels: {usePointStyle: true, boxWidth: 8}},
          tooltip: {
            callbacks: {label: context => this.tooltipLabel(context)},
            ...(this.sortTooltip ? {itemSort: (first, second) => (second.parsed.y ?? 0) - (first.parsed.y ?? 0)} : {}),
          },
        },
      };
      if (this.isDoughnut) return {...options, cutout: '58%'};

      const valueAxis = (format, extra = {}) => ({
        beginAtZero: true,
        stacked: this.stacked,
        grid: {color: '#eef1f6'},
        border: {display: false},
        ticks: {callback: value => (format === 'money' ? moneyCompact(value) : formatValue(format, value))},
        ...extra,
      });
      const categoryAxis = {
        stacked: this.stacked, grid: {display: false}, border: {color: '#dbe1ec'},
        ...(this.labels.length > 24 ? {ticks: {maxTicksLimit: 12, maxRotation: 0}} : {}),
      };
      return {
        ...options,
        indexAxis: this.horizontal ? 'y' : 'x',
        scales: {
          [this.horizontal ? 'y' : 'x']: categoryAxis,
          [this.horizontal ? 'x' : 'y']: valueAxis(this.valueFormat),
          ...(hasSecondAxis ? {y1: valueAxis(this.secondaryFormat, {position: 'right', grid: {drawOnChartArea: false}})} : {}),
        },
      };
    },
  },
  methods: {
    style(dataset, index) {
      const type = dataset.type ?? this.type;
      const color = dataset.color ?? colorAt(index);
      const base = {...dataset, order: type === 'line' ? 0 : 1};

      if (type === 'doughnut') {
        return {...base, backgroundColor: dataset.colors ?? PALETTE, borderColor: '#ffffff', borderWidth: 2};
      }
      if (type === 'line') {
        return {
          ...base, borderColor: color, backgroundColor: withAlpha(color, 0.12), borderWidth: 2, tension: 0.3,
          pointRadius: dataset.data.length > 40 ? 0 : 3, pointHoverRadius: 5, borderDash: dataset.dashed ? [6, 4] : [],
          fill: dataset.fill ?? false,
        };
      }
      return {...base, backgroundColor: dataset.colors ?? color, borderRadius: 4, maxBarThickness: 38};
    },
    tooltipLabel(context) {
      const format = context.dataset.yAxisID === 'y1' ? this.secondaryFormat : this.valueFormat;
      const value = this.horizontal ? context.parsed.x : (this.isDoughnut ? context.parsed : context.parsed.y);
      if (!this.isDoughnut) return ` ${context.dataset.label}: ${formatValue(format, value)}`;
      const total = context.dataset.data.reduce((sum, current) => sum + current, 0);
      return ` ${context.label}: ${formatValue(format, value)} (${formatValue('percent', total ? value / total * 100 : 0)})`;
    },
  },
};

</script>

<style lang="stylus" scoped>
.statistics-chart
  position relative
  width 100%
</style>
